import { useAuth0 } from '@auth0/auth0-react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import {
  TradingMessageFeedbackRating,
  buildTradingMessageFeedbackKey,
  useTradingMessageFeedbackStore,
} from 'hooks/state/useTradingMessageFeedbackStore';
import { TradingMessage } from 'pages/trading/model';
import { MouseEvent, useContext, useMemo } from 'react';
import { toast } from 'sonner';
import { FormattedMessage, useIntl } from 'react-intl';
import { trackMatomoEvent } from 'services/matomo.service';
import { TbIcon, TbIconProps } from 'components/Figma';

const trackMatomoFeedback = ({
  rating,
  pageUrl,
  messageId,
  analyserId,
  accountNumber,
  userId,
}: {
  rating: TradingMessageFeedbackRating;
  pageUrl: string;
  messageId: number | undefined;
  analyserId: string | undefined;
  accountNumber: string | undefined;
  userId: string | undefined;
}) => {
  const payload = {
    pageUrl,
    accountNumber: accountNumber ?? 'unknown',
    userId: userId ?? 'unknown',
    analyserId,
    messageId,
  };
  trackMatomoEvent({
    category: 'Trading',
    action: `MessageFeedback_${rating}`,
    name: JSON.stringify(payload),
  });
};

const RATING_META: Record<
  TradingMessageFeedbackRating,
  {
    labelId: string;
    colorClassName: string;
    hoverClassName: string;
    iconOutlined: TbIconProps['name'];
    iconFilled: TbIconProps['name'];
  }
> = {
  VALUABLE: {
    labelId: 'trading.message_feedback.button.valuable',
    colorClassName: 'text-laurel-600',
    hoverClassName: 'hover:bg-laurel-600/10',
    iconOutlined: 'ThumbsUpOutlined',
    iconFilled: 'ThumbsUpFilled',
  },
  NOT_VALUABLE: {
    labelId: 'trading.message_feedback.button.not_valuable',
    colorClassName: 'text-coral-500',
    hoverClassName: 'hover:bg-coral-500/10',
    iconOutlined: 'ThumbsDownOutlined',
    iconFilled: 'ThumbsDownFilled',
  },
  FAULTY: {
    labelId: 'trading.message_feedback.button.faulty',
    colorClassName: 'text-ash-900',
    hoverClassName: 'hover:bg-ash-900/10',
    iconOutlined: 'FlagOutlined',
    iconFilled: 'FlagFilled',
  },
};

export function TradingMessageFeedback({ message }: { message: TradingMessage }) {
  const intl = useIntl();
  const { user } = useAuth0();
  const { accountNumber } = useContext(TenantConfigContext);

  const entryKey = useMemo(
    () => buildTradingMessageFeedbackKey(accountNumber, user?.sub, message.messageId),
    [accountNumber, user?.sub, message.messageId]
  );

  const ratingEntry = useTradingMessageFeedbackStore(state => state.entries[entryKey]);
  const recordRating = useTradingMessageFeedbackStore(state => state.recordRating);
  const rating = ratingEntry?.rating ?? null;

  const handleClick = (newRating: TradingMessageFeedbackRating) => (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (rating) return;

    const { didStore } = recordRating(entryKey, newRating);
    if (!didStore) {
      return;
    }

    toast.success(intl.formatMessage({ id: 'trading.message_feedback.toast.thanks' }));

    trackMatomoFeedback({
      rating: newRating,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      messageId: message.messageId,
      analyserId: message.analyserId,
      accountNumber,
      userId: user?.sub,
    });
  };

  if (rating) {
    const { labelId, colorClassName, iconFilled } = RATING_META[rating];
    const ratingLabel = (
      <span className={`ml-1 inline-flex items-center gap-1 font-bold ${colorClassName}`}>
        <TbIcon name={iconFilled} />
        {intl.formatMessage({ id: labelId })}
      </span>
    );
    return (
      <div className="flex items-center justify-end text-xs text-ash-700" onClick={e => e.stopPropagation()}>
        <FormattedMessage id="trading.message_feedback.marked_as" values={{ rating: ratingLabel }} />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-3" onClick={e => e.stopPropagation()}>
      <span className="text-xs text-ash-700 whitespace-nowrap">
        <FormattedMessage id="trading.message_feedback.question" />
      </span>
      <div className="flex items-center gap-2">
        {(Object.keys(RATING_META) as TradingMessageFeedbackRating[]).map(r => {
          const { labelId, colorClassName, hoverClassName, iconOutlined, iconFilled } = RATING_META[r];
          return (
            <button
              key={r}
              type="button"
              className={`group flex items-center gap-1.5 px-2 py-1 rounded-none bg-transparent transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ash-900 ${colorClassName} ${hoverClassName}`}
              onClick={handleClick(r)}
            >
              <TbIcon name={iconOutlined} className="group-hover:hidden" />
              <TbIcon name={iconFilled} className="hidden group-hover:block" />
              <span className="text-xs font-bold whitespace-nowrap">
                <FormattedMessage id={labelId} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
